import { useCallback, useMemo, useState } from 'react';
import type { AddToolOutputArgs } from '../ops/core';
import type {
    ClearContoursOutcome,
    JobActionOutcome,
    LoadSeriesOutcome,
    ModelActionOutcome,
    MutationOutcome,
    SendStructureSetOutcome,
    SetStructureColorOutcome,
    StructureChangeOutcome,
} from '../contract';

/** Every gated tool's outcome shares the applied/rejected/failed statuses. */
type GateOutcome =
    | MutationOutcome
    | ModelActionOutcome
    | LoadSeriesOutcome
    | ClearContoursOutcome
    | JobActionOutcome
    | SendStructureSetOutcome
    | SetStructureColorOutcome
    | StructureChangeOutcome;

export type ProposalStatus =
    | 'pending'
    | 'applying'
    | 'applied'
    | 'rejected'
    | 'failed';

/** What a card needs to render + the gate handlers, keyed by toolCallId. */
export interface ProposalChannelView<P> {
    readonly proposal: P;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
}

/** The cross-op controls a proposal channel exposes (proposal type erased) —
 *  the surface the hook drives uniformly over every mutating tool. */
export interface ChannelControls {
    readonly views: Record<string, ProposalChannelView<unknown>>;
    apply(toolCallId: string): Promise<void>;
    reject(toolCallId: string): void;
    cancel(toolCallId: string, reason?: string): void;
    showPreview(toolCallId: string): void;
    hidePreview(toolCallId: string): void;
}

/** A gated proposal: the clinician's Apply/Reject produces the structured
 *  outcome the model loop is resumed with. */
interface GatedProposal<O> {
    readonly toolCallId: string;
    apply(): Promise<O>;
    reject(): O;
    /** Show/clear a translucent overlay of the previewed geometry (card hover). */
    showPreview(): void;
    hidePreview(): void;
}

/**
 * One mutating tool's client-side proposal lifecycle (register → pending →
 * applying → settled). Identical for every op (the status machine and the
 * pending-only guard live ONCE here), instantiated per tool. The cards read
 * `views`; a non-empty pending/applying set is this channel's contribution to
 * the ≤1-pending signal — the cross-op OR is computed by the caller so the
 * invariant spans every mutating tool. `emit` resumes the model loop with
 * the structured outcome.
 */
export function useProposalChannel<
    O extends GateOutcome,
    P extends GatedProposal<O>,
>(toolName: string, emit: (args: AddToolOutputArgs) => void) {
    const [views, setViews] = useState<Record<string, ProposalChannelView<P>>>(
        {}
    );

    const register = useCallback((proposal: P) => {
        setViews((prev) => ({
            ...prev,
            [proposal.toolCallId]: { proposal, status: 'pending' },
        }));
    }, []);

    const settle = useCallback(
        (toolCallId: string, outcome: GateOutcome) => {
            emit({ tool: toolName, toolCallId, output: outcome });
            setViews((prev) => {
                const entry = prev[toolCallId];
                if (!entry) {
                    return prev;
                }
                const status: ProposalStatus =
                    outcome.status === 'applied'
                        ? 'applied'
                        : outcome.status === 'rejected'
                          ? 'rejected'
                          : 'failed';
                return {
                    ...prev,
                    [toolCallId]: {
                        ...entry,
                        status,
                        failureReason:
                            outcome.status === 'failed'
                                ? outcome.reason
                                : undefined,
                    },
                };
            });
        },
        [emit, toolName]
    );

    const apply = useCallback(
        async (toolCallId: string) => {
            const entry = views[toolCallId];
            if (!entry || entry.status !== 'pending') {
                return;
            }
            // Clear any hover overlay before commit adopts the borrowed texture.
            entry.proposal.hidePreview();
            setViews((prev) =>
                prev[toolCallId]
                    ? {
                          ...prev,
                          [toolCallId]: {
                              ...prev[toolCallId],
                              status: 'applying',
                          },
                      }
                    : prev
            );
            settle(toolCallId, await entry.proposal.apply());
        },
        [views, settle]
    );

    const reject = useCallback(
        (toolCallId: string) => {
            const entry = views[toolCallId];
            if (!entry || entry.status !== 'pending') {
                return;
            }
            // reject() clears the overlay itself, then frees the handle.
            settle(toolCallId, entry.proposal.reject());
        },
        [views, settle]
    );

    // Settle the card WITHOUT emitting — used when the repaired history
    // already carries the cancelled outcome. Pending-only: Apply/Reject on
    // the card stay blocked afterwards.
    const cancel = useCallback(
        (
            toolCallId: string,
            reason = 'cancelled — a new message was sent before a decision'
        ) => {
            const entry = views[toolCallId];
            if (!entry || entry.status !== 'pending') {
                return;
            }
            // reject() clears the hover overlay and frees the eagerly-computed
            // texture — guarded so a racing apply that already claimed the
            // handle is never freed. Outcome discarded (no emit).
            entry.proposal.reject();
            setViews((prev) => {
                const current = prev[toolCallId];
                if (!current || current.status !== 'pending') {
                    return prev;
                }
                return {
                    ...prev,
                    [toolCallId]: {
                        ...current,
                        status: 'failed',
                        failureReason: reason,
                    },
                };
            });
        },
        [views]
    );

    // Hover preview: show only a still-pending proposal (a settled/applying
    // card's geometry may be adopted/freed). Hide is always allowed so a card
    // that settles while hovered still clears its overlay.
    const showPreview = useCallback(
        (toolCallId: string) => {
            const entry = views[toolCallId];
            if (!entry || entry.status !== 'pending') {
                return;
            }
            entry.proposal.showPreview();
        },
        [views]
    );

    const hidePreview = useCallback(
        (toolCallId: string) => {
            views[toolCallId]?.proposal.hidePreview();
        },
        [views]
    );

    // Memoized so the channel object identity is stable until `views` changes
    // (the callbacks are already useCallback-stable). Without this the channel
    // is a fresh literal every render, which would make the caller's `channels`
    // map — and the `pendingExists`/`proposalsByTool`/`mutatingOps` memos keyed
    // on it — recompute on every streamed token.
    return useMemo(
        () => ({
            views,
            register,
            apply,
            reject,
            cancel,
            showPreview,
            hidePreview,
        }),
        [views, register, apply, reject, cancel, showPreview, hidePreview]
    );
}
